import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const EXPENSE_TRACKER_SIDEBAR_WIDTH = 480;
export const EXPENSE_TRACKER_ANIM_MS = 280;
export const EXPENSE_TRACKER_ANIM_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

export function expenseTrackerFixedInset(open, viewportWidth) {
  if (!open) return 0;
  const width = Number(viewportWidth);
  if (Number.isFinite(width) && width <= EXPENSE_TRACKER_SIDEBAR_WIDTH) return width;
  return EXPENSE_TRACKER_SIDEBAR_WIDTH;
}

export function nextExpenseTrackerOpen(open) {
  return !open;
}

/**
 * Which screen's footer is pinned to the viewport bottom right now. Only the focused
 * screen that actually renders a pinned footer claims it, so screens with hideFooter,
 * pinFooter={false} or a locked viewport leave it false. Releasing is owner-checked, so
 * a blurring screen cannot clear the claim the newly focused screen just made.
 */
export const FOOTER_PIN_NONE = { owner: null, pinned: false };

export function claimFooterPin(state, owner, pinned) {
  const next = !!pinned;
  if (state && state.owner === owner && state.pinned === next) return state;
  return { owner, pinned: next };
}

export function releaseFooterPin(state, owner) {
  return state && state.owner === owner ? FOOTER_PIN_NONE : state;
}

/** Bottom offset for fixed corner widgets (Review VRC) so they sit above a pinned footer. */
export function fixedWidgetBottomInset(footerPinned, footerHeight) {
  return footerPinned ? Number(footerHeight) || 0 : 0;
}

const AppLayoutContext = createContext({
  headerHeight: 100,
  setHeaderHeight: () => {},
  navVisible: true,
  setNavVisible: () => {},
  mobileNavContentWidth: 0,
  setMobileNavContentWidth: () => {},
  pageSchemeOverride: null,
  setPageSchemeOverride: () => {},
  expenseTrackerOpen: false,
  setExpenseTrackerOpen: () => {},
  toggleExpenseTracker: () => {},
  footerPinned: false,
  claimFooterPinned: () => {},
  releaseFooterPinned: () => {},
});

export function AppLayoutProvider({ children }) {
  const [headerHeight, setHeaderHeight] = useState(100);
  const [navVisible, setNavVisible] = useState(true);
  const [mobileNavContentWidth, setMobileNavContentWidth] = useState(0);
  const [pageSchemeOverride, setPageSchemeOverride] = useState(null);
  const [expenseTrackerOpen, setExpenseTrackerOpen] = useState(false);
  const toggleExpenseTracker = useCallback(() => {
    setExpenseTrackerOpen(nextExpenseTrackerOpen);
  }, []);
  const [footerPin, setFooterPin] = useState(FOOTER_PIN_NONE);
  const claimFooterPinned = useCallback((owner, pinned) => {
    setFooterPin((prev) => claimFooterPin(prev, owner, pinned));
  }, []);
  const releaseFooterPinned = useCallback((owner) => {
    setFooterPin((prev) => releaseFooterPin(prev, owner));
  }, []);
  const footerPinned = footerPin.pinned;
  const value = useMemo(
    () => ({
      headerHeight,
      setHeaderHeight,
      navVisible,
      setNavVisible,
      mobileNavContentWidth,
      setMobileNavContentWidth,
      pageSchemeOverride,
      setPageSchemeOverride,
      expenseTrackerOpen,
      setExpenseTrackerOpen,
      toggleExpenseTracker,
      footerPinned,
      claimFooterPinned,
      releaseFooterPinned,
    }),
    [headerHeight, navVisible, mobileNavContentWidth, pageSchemeOverride, expenseTrackerOpen, toggleExpenseTracker, footerPinned, claimFooterPinned, releaseFooterPinned],
  );
  return (
    <AppLayoutContext.Provider value={value}>
      {children}
    </AppLayoutContext.Provider>
  );
}

export function useAppLayout() {
  return useContext(AppLayoutContext);
}

export function useViewportWidth() {
  const [vw, setVw] = useState(() => (
    typeof window === 'undefined' ? EXPENSE_TRACKER_SIDEBAR_WIDTH : window.innerWidth
  ));
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return vw;
}

export function useExpenseTrackerInset() {
  const { expenseTrackerOpen } = useAppLayout();
  const vw = useViewportWidth();
  return expenseTrackerFixedInset(expenseTrackerOpen, vw);
}

/** Approximate footer block height when pinned to viewport bottom. */
export const FIXED_FOOTER_HEIGHT = 92;
